import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import aiRobotIcon from '../../static/icons/ai-robot-icon.svg';
import AddIcon from '../../static/icons/add-icon.svg';
import TextIcon from '../../static/icons/text.svg';
import StimulusModal from '../Stimulus/StimulusModal';
import FilePreviewModal from '../Stimulus/FilePreviewModal';
import RemovableStimulusPill from '../Stimulus/RemovableStimulusPill';
import StimulusPillIcon from '../Stimulus/StimulusPillIcon';
import { isRichTextEmpty, stripHtml } from '../Stimulus/stimulusHelpers';
import {
  AddQuestionFieldLabel,
  AddQuestionInput,
  AddQuestionMascot,
  AddQuestionPanel,
  AddQuestionTextarea,
  AddStimulusButton,
  AddStimulusIcon,
  BulkUploadSectionTitle,
} from '../BulkUpload/styles';

export const AddQuestionDraftPanel = ({
  idPrefix = 'add-question',
  title = '',
  question = '',
  stimulusText = '',
  stimulusFiles = [],
  onTitleChange,
  onQuestionChange,
  onTitleBlur,
  onQuestionBlur,
  onStimulusTextChange,
  onStimulusFilesChange,
  showMascot = true,
  fillHeight = false,
}) => {
  const [titleValue, setTitleValue] = useState(title);
  const [questionValue, setQuestionValue] = useState(question);
  const [isStimulusModalOpen, setIsStimulusModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    setTitleValue(title);
  }, [title]);

  useEffect(() => {
    setQuestionValue(question);
  }, [question]);

  const stimulusChips = useMemo(() => {
    const items = [];
    if (stimulusText && !isRichTextEmpty(stimulusText)) {
      items.push({
        id: 'stimulus-text',
        type: 'text',
        label: 'Text 1',
        fileType: 'TEXT',
        name: 'Text 1',
        html: stimulusText,
      });
    }
    const files = Array.isArray(stimulusFiles) ? stimulusFiles : [];
    let imageCount = 0;
    let pdfCount = 0;
    files.forEach((file, fileIndex) => {
      if (!file) return;
      if (file.fileType === 'PDF' || /\.pdf$/i.test(file.name || '')) {
        pdfCount += 1;
        items.push({
          id: file.id || `pdf-${pdfCount}`,
          fileIndex,
          type: 'pdf',
          label: file.name || `PDF ${pdfCount}`,
          fileType: 'PDF',
          name: file.name || `PDF ${pdfCount}`,
          url: file.url,
        });
        return;
      }
      imageCount += 1;
      items.push({
        id: file.id || `image-${imageCount}`,
        fileIndex,
        type: 'image',
        label: `Image ${imageCount}`,
        fileType: 'IMAGE',
        name: file.name || `Image ${imageCount}`,
        url: file.url,
      });
    });
    return items;
  }, [stimulusText, stimulusFiles]);

  const removeStimulusItem = (item) => {
    if (item.type === 'text') {
      onStimulusTextChange?.('');
      return;
    }
    const files = Array.isArray(stimulusFiles) ? stimulusFiles : [];
    onStimulusFilesChange?.(
      files.filter((_, index) => index !== item.fileIndex),
    );
  };

  return (
    <AddQuestionPanel $fillHeight={fillHeight}>
      <BulkUploadSectionTitle>Add question</BulkUploadSectionTitle>
      <AddQuestionFieldLabel htmlFor={`${idPrefix}-title`}>
        Title
      </AddQuestionFieldLabel>
      <AddQuestionInput
        id={`${idPrefix}-title`}
        placeholder="Document Title"
        value={titleValue}
        onChange={(e) => {
          setTitleValue(e.target.value);
          onTitleChange?.(e.target.value);
        }}
        onBlur={() => onTitleBlur?.(titleValue)}
      />
      <AddQuestionFieldLabel htmlFor={`${idPrefix}-question`}>
        Question
      </AddQuestionFieldLabel>
      <AddQuestionTextarea
        id={`${idPrefix}-question`}
        placeholder="Add a question here"
        value={questionValue}
        onChange={(e) => {
          setQuestionValue(e.target.value);
          onQuestionChange?.(e.target.value);
        }}
        onBlur={() => onQuestionBlur?.(questionValue)}
      />
      <AddQuestionFieldLabel as="div">
        Stimulus <span style={{ color: '#918b97', fontWeight: 400 }}>(optional)</span>
      </AddQuestionFieldLabel>
      <StimulusRow>
        {stimulusChips.length > 0 && (
          <ChipList>
            {stimulusChips.map((item) => (
              <RemovableStimulusPill
                key={item.id}
                label={item.label}
                onRemove={() => removeStimulusItem(item)}
                testId={`remove-stimulus-${item.type}-${item.id}`}
              >
                <StimulusChip
                  type="button"
                  onClick={() => setPreviewFile(item)}
                  title={
                    item.type === 'text' ? stripHtml(stimulusText) : item.label
                  }
                >
                  <StimulusPillIcon
                    item={item}
                    textIcon={TextIcon}
                    Thumb={ChipImage}
                    Icon={ChipIcon}
                  />
                  <ChipLabel>{item.label}</ChipLabel>
                </StimulusChip>
              </RemovableStimulusPill>
            ))}
          </ChipList>
        )}
        <AddStimulusButton type="button" onClick={() => setIsStimulusModalOpen(true)}>
          <AddStimulusIcon src={AddIcon} alt="" aria-hidden="true" />
          {stimulusChips.length > 0 ? 'Edit Stimulus' : 'Add Stimulus'}
        </AddStimulusButton>
      </StimulusRow>
      {showMascot && <AddQuestionMascot src={aiRobotIcon} alt="" aria-hidden="true" />}
      <StimulusModal
        open={isStimulusModalOpen}
        onClose={() => setIsStimulusModalOpen(false)}
        initialStimulusText={stimulusText}
        initialStimulusFiles={stimulusFiles}
        onSave={({ stimulusText: nextText, stimulusFiles: nextFiles }) => {
          onStimulusTextChange?.(nextText);
          onStimulusFilesChange?.(nextFiles);
        }}
      />
      <FilePreviewModal
        open={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />
    </AddQuestionPanel>
  );
};

const StimulusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const StimulusChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 180px;
  padding: 6px 10px;
  border: 1px solid rgba(201, 198, 204, 0.8);
  border-radius: 999px;
  background: #fff;
  cursor: pointer;

  &:hover {
    border-color: #7200e0;
  }
`;

const ChipIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const ChipImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ChipLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1e252a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
